import { inject, Injectable } from '@angular/core';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import {
    GetHistoricalData,
    GetHistoricalDataSchema,
    Rate,
} from './iq-graph-rates-time-transformation.models';
import { isNumber } from 'lodash';
type ContributionsPayload = {
    contributions: any[];
    perLoad: boolean;
    mileage: number;
};

@Injectable({
    providedIn: 'root',
})
export class IqGraphRatesTimeTransformationService {
    private readonly _bus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );
    private _initialized = false;

    constructor() {}

    //#region Transformations
    private calculateRateWithFuel(
        rate: Rate,
        mileage: number,
        payload: GetHistoricalData,
        shortHaul: boolean,
    ) {
        if (shortHaul) {
        }
        const historicalFuelRate = shortHaul
            ? rate.value + rate.fuelPerTrip
            : payload.perLoad
            ? rate.value * mileage + rate.fuelPerTrip
            : rate.value + rate.fuelPerMile;

        const customFuelRate = shortHaul
            ? rate.value + payload.tripFuel
            : payload.perLoad
            ? rate.value * mileage + payload.tripFuel
            : rate.value + payload.mileFuel;

        switch (payload.fuelType) {
            case 'DAILY':
                return historicalFuelRate;
            case 'CUSTOM':
                return customFuelRate;
            case 'NONE':
                return payload.perLoad && !shortHaul
                    ? rate.value * mileage
                    : rate.value;
            default:
                return rate.value;
        }
    }
    /**
     * Takes an array of rates and calculates with Fuel and Mileage
     */
    calculateGraphHistoricalAverageRates(transformValue: GetHistoricalData) {
        try {
            const check: GetHistoricalData =
                GetHistoricalDataSchema.parse(transformValue);
            const rates = check.average;

            const calculatedRates = rates.map((rate) => {
                return {
                    ...rate,
                    value: this.calculateRateWithFuel(
                        rate,
                        rate.mileage,
                        check,
                        check.shortHaul,
                    ),
                };
            });
            return calculatedRates;
        } catch (e) {
            console.error(e);
            return null;
        }
    }
    /**
     * Takes an array of rates and calculates with Fuel and Mileage
     */
    calculateGraphHistoricalHighRates(transformValue: GetHistoricalData) {
        try {
            const check: GetHistoricalData =
                GetHistoricalDataSchema.parse(transformValue);

            const rates = check.high;

            const calculatedRates = rates.map((rate) => {
                return {
                    ...rate,
                    value: this.calculateRateWithFuel(
                        rate,
                        rate.mileage,
                        check,
                        check.shortHaul,
                    ),
                };
            });
            return calculatedRates;
        } catch (e) {
            console.error(e);
            return null;
        }
    }
    /**
     * Takes an array of rates and calculates with Fuel and Mileage
     */
    calculateGraphHistoricalLowRates(transformValue: GetHistoricalData) {
        try {
            const check: GetHistoricalData =
                GetHistoricalDataSchema.parse(transformValue);

            const rates = check.low;

            const calculatedRates = rates.map((rate) => {
                return {
                    ...rate,
                    value: this.calculateRateWithFuel(
                        rate,
                        rate.mileage,
                        check,
                        check.shortHaul,
                    ),
                };
            });
            return calculatedRates;
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    private forecastCalculation(
        value,
        mileage,
        currentFuel,
        perLoad,
        shortHaul,
    ) {
        const calculatedRate = shortHaul
            ? value + currentFuel
            : perLoad
            ? value * mileage + currentFuel
            : value + currentFuel;
        return perLoad ? Math.round(calculatedRate) : calculatedRate;
    }
    /**
     * Takes an array of rates and calculates with Fuel and Mileage
     */
    calculateGraphForecastLowRates(transformValue: GetHistoricalData) {
        try {
            const check: GetHistoricalData =
                GetHistoricalDataSchema.parse(transformValue);

            const rates = check.low;
            const currentFuel =
                check.perLoad || check.shortHaul
                    ? check.tripFuel
                    : check.mileFuel;

            const calculatedRates = rates.map((rate) => {
                return {
                    ...rate,
                    value: this.forecastCalculation(
                        rate.value,
                        rate.mileage,
                        currentFuel,
                        check.perLoad,
                        check.shortHaul,
                    ),
                };
            });
            return calculatedRates;
        } catch (e) {
            console.error(e);
            return null;
        }
    }
    calculateGraphForecastAverageRates(transformValue: GetHistoricalData) {
        try {
            const check: GetHistoricalData =
                GetHistoricalDataSchema.parse(transformValue);

            const rates = check.average;
            const currentFuel =
                check.perLoad || check.shortHaul
                    ? check.tripFuel
                    : check.mileFuel;
            const calculatedRates = rates.map((rate) => {
                return {
                    ...rate,
                    value: this.forecastCalculation(
                        rate.value,
                        rate.mileage,
                        currentFuel,
                        check.perLoad,
                        check.shortHaul,
                    ),
                };
            });
            return calculatedRates;
        } catch (e) {
            console.error(e);
            return null;
        }
    }
    /**
     * Takes an array of rates and calculates with Fuel and Mileage
     */
    calculateGraphForecastHighRates(transformValue: GetHistoricalData) {
        try {
            const check: GetHistoricalData =
                GetHistoricalDataSchema.parse(transformValue);

            const rates = check.high;
            const currentFuel =
                check.perLoad || check.shortHaul
                    ? check.tripFuel
                    : check.mileFuel;
            const calculatedRates = rates.map((rate) => {
                return {
                    ...rate,
                    value: this.forecastCalculation(
                        rate.value,
                        rate.mileage,
                        currentFuel,
                        check.perLoad,
                        check.shortHaul,
                    ),
                };
            });
            return calculatedRates;
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    calculateGraphCurrentAverageRates(transformValue: GetHistoricalData) {
        try {
            const check: GetHistoricalData =
                GetHistoricalDataSchema.parse(transformValue);

            const rate = check.average[0].value;
            const shortHaul = check.shortHaul;
            const mileage =
                check.perLoad && !shortHaul ? check.high[0].mileage : 1;
            const currentFuel =
                check.perLoad || shortHaul ? check.tripFuel : check.mileFuel;

            const calculatedRates = [
                {
                    value: rate * mileage + currentFuel,
                    date: new Date().toISOString(),
                    mileage,
                    fuelPerMile: 0,
                    fuelPerTrip: 0,
                },
            ];
            return calculatedRates;
        } catch (e) {
            console.error(e);
            return null;
        }
    }
    calculateGraphCurrentLowRates(transformValue: GetHistoricalData) {
        try {
            const check: GetHistoricalData =
                GetHistoricalDataSchema.parse(transformValue);

            const rate = check.low[0].value;

            const mileage =
                check.perLoad && !check.shortHaul ? check.high[0].mileage : 1;
            const currentFuel =
                check.perLoad || check.shortHaul
                    ? check.tripFuel
                    : check.mileFuel;
            const calculatedRates = [
                {
                    value: rate * mileage + currentFuel,
                    date: new Date().toISOString(),
                    mileage,
                    fuelPerMile: 0,
                    fuelPerTrip: 0,
                },
            ];
            return calculatedRates;
        } catch (e) {
            console.error(e);
            return null;
        }
    }
    calculateGraphCurrentHighRates(transformValue: GetHistoricalData) {
        try {
            const check: GetHistoricalData =
                GetHistoricalDataSchema.parse(transformValue);

            const rate = check.high[0].value;
            const mileage =
                check.perLoad && !check.shortHaul ? check.high[0].mileage : 1;
            const currentFuel =
                check.perLoad || check.shortHaul
                    ? check.tripFuel
                    : check.mileFuel;
            const calculatedRates = [
                {
                    value: rate * mileage + currentFuel,
                    date: new Date().toISOString(),
                    mileage,
                    fuelPerMile: 0,
                    fuelPerTrip: 0,
                },
            ];
            return calculatedRates;
        } catch (e) {
            console.error(e);
            return null;
        }
    }
    //#endregion
    calculateContributions(transformValue: ContributionsPayload) {
        if (transformValue.perLoad) {
            return transformValue.contributions.map((contribution) => {
                return {
                    ...contribution,
                    value: contribution.value / transformValue.mileage,
                };
            });
        }
        return transformValue.contributions;
    }

    //#region Registrations
    /**
     * Maps all the service functions to a transformation object that can be passed into
     * ejs.
     *
     * @returns an object of all the available transformations and other function to be leveraged in templates
     */
    getTransformations() {
        return {
            calculateGraphHistoricalAverageRates: (
                transformValue: GetHistoricalData,
            ) => this.calculateGraphHistoricalAverageRates(transformValue),
            calculateGraphHistoricalHighRates: (
                transformValue: GetHistoricalData,
            ) => this.calculateGraphHistoricalHighRates(transformValue),
            calculateGraphHistoricalLowRates: (
                transformValue: GetHistoricalData,
            ) => this.calculateGraphHistoricalLowRates(transformValue),
            calculateGraphCurrentHighRates: (
                transformValue: GetHistoricalData,
            ) => this.calculateGraphCurrentHighRates(transformValue),
            calculateGraphCurrentLowRates: (
                transformValue: GetHistoricalData,
            ) => this.calculateGraphCurrentLowRates(transformValue),
            calculateGraphCurrentAverageRates: (
                transformValue: GetHistoricalData,
            ) => this.calculateGraphCurrentAverageRates(transformValue),
            calculateGraphForecastHighRates: (
                transformValue: GetHistoricalData,
            ) => this.calculateGraphForecastHighRates(transformValue),
            calculateGraphForecastLowRates: (
                transformValue: GetHistoricalData,
            ) => this.calculateGraphForecastLowRates(transformValue),
            calculateGraphForecastAverageRates: (
                transformValue: GetHistoricalData,
            ) => this.calculateGraphForecastAverageRates(transformValue),
            calculateContributions: (transformValue: ContributionsPayload) =>
                this.calculateContributions(transformValue),
        };
    }
    /**
     * Initializes the transformation service by adding all the transformation maps to the event bus.
     */
    initialize() {
        if (this._initialized) return;
        this._bus.dispatch({
            name: 'addTransformationMap',
            payload: this.getTransformations(),
        });

        this._initialized = true;
    }

    //#endregion
}
